import { Translatable } from '@fmnts/api/shared';
import { extractProperties } from '@fmnts/core/object';
import { InlineTranslateable } from '@fmnts/i18n';
import { IFieldCondition } from '../api-model/condition';
import { IApiFormBaseField } from '../api-model/forms.api';

// ==================================================================
// Interfaces
// ==================================================================

export interface IInputField {
  defaultValue?: unknown | undefined;
  /** @deprecated */
  required?: boolean | undefined;
  scope: string | undefined;
  validators: IValidatorConfig[];
}

export interface ISelectInputField extends IInputField {
  items: ISelectInputItem[];
  itemConditions?: { [key: string]: IFieldCondition };
  hasTypeAhead?: boolean;
}

export interface ITextInputField extends IInputField {
  autocapitalize?: string | undefined;
  keyboardType?: string | undefined;
  format?: string | undefined;
}

export interface ITextboxField {
  text: string | Translatable<string>;
  assets?: unknown[];
  image?: string | undefined;
}

export interface IActionField {
  action: string;
  actionConfig: unknown;
}

export interface ISelectInputItem {
  label: InlineTranslateable;
  value: any;
  description?: InlineTranslateable | undefined;
  image?: string | undefined;
}

export interface IValidatorConfig {
  /**
   * Name of the validator
   */
  name: string;
  /**
   * Arguments for the validator
   */
  args: Record<string, any> | undefined;
  /**
   * Optional messages for validation results. This
   * may also include "customized" properties are
   * referenced from the conditional args. This is useful
   * to prevent having to add the same translations
   * multiple times
   */
  messages?: Record<string, InlineTranslateable>;
}

const extractISpecific = extractProperties<IApiFormBaseField>(
  {
    key: false,
    label: false,
    attrs: false,
    widget: false,
    style: false,
    condition: false,
  },
  { extractUntyped: true },
);

export type WidgetType = 'input' | 'textbox' | 'action' | 'unknown';

export type FieldWidget =
  | IInputField
  | ITextboxField
  | IActionField
  | undefined;

// ==================================================================
// Base Form Field Class
// ==================================================================
export class FormField<
  TWidgetType extends WidgetType = WidgetType,
  T extends FieldWidget = FieldWidget,
> implements IApiFormBaseField
{
  key: string;
  label: string | Translatable<string> | undefined;
  attrs?: Record<string, any> | undefined;
  condition: IFieldCondition | undefined;
  style?: string | undefined;
  widget: string;

  widgetOptions: T;
  widgetType: TWidgetType | 'unknown';

  constructor(
    dto: Readonly<IApiFormBaseField>,
    fieldOptions: T,
    fieldType: TWidgetType | 'unknown' = 'unknown',
  ) {
    this.key = dto.key;
    this.label = dto.label;
    this.widget = dto.widget ?? '';
    this.style = dto.style;
    this.condition = dto.condition;

    this.widgetOptions = fieldOptions;
    this.widgetType = fieldType;

    this.attrs = extractISpecific<IApiFormBaseField>({
      ...dto,
      ...fieldOptions,
    });
  }
}

export class InputField implements IInputField {
  defaultValue?: unknown;
  /** @deprecated */
  required?: boolean | undefined;
  scope: string | undefined;
  validators: IValidatorConfig[];

  constructor(obj: Readonly<IInputField>) {
    this.defaultValue = obj.defaultValue;
    this.scope = obj.scope || 'form';
    this.validators = obj.validators || [];

    // @deprecated This shouldn't be sent as an input property, but
    // instead within the validators.
    if (obj.required) {
      this.validators.push({ name: 'required', args: undefined });
    }
  }
}

/**
 * Extension of a FieldInput for a Select field.
 */
export class SelectInputField extends InputField implements ISelectInputField {
  items: ISelectInputItem[];
  itemConditions?: { [key: string]: IFieldCondition };
  hasTypeAhead?: boolean;

  constructor(obj: Readonly<ISelectInputField>) {
    super(obj);
    this.items = obj.items;
    this.itemConditions = obj.itemConditions;
    this.hasTypeAhead = obj.hasTypeAhead;
  }
}

/**
 * Extension of a FieldInput for a Text field.
 */
export class TextInputField extends InputField implements ITextInputField {
  autocapitalize?: string | undefined;
  keyboardType?: string | undefined;
  format?: string | undefined;

  constructor(obj: Readonly<ITextInputField>) {
    super(obj);
    this.autocapitalize = obj.autocapitalize;
    this.keyboardType = obj.keyboardType;
    this.format = obj.format;
  }
}

export class TextboxField implements ITextboxField {
  text: string | Translatable<string>;
  assets: unknown[];
  image: string | undefined;

  constructor(obj: Readonly<ITextboxField>) {
    this.text = obj.text;
    this.assets = obj.assets || [];
    this.image = obj.image || '';
  }
}

export class ActionField {
  action: string;
  actionConfig: unknown;

  constructor(dto: Readonly<IActionField>) {
    this.action = dto.action;
    this.actionConfig = dto.actionConfig;
  }
}
